import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy, RefreshCw } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { cn } from "../../../../lib/utils";
import { FillsTable } from "../components/FillsTable";
import { WindowPicker } from "../components/WindowPicker";
import { Pagination } from "../components/Pagination";
import { ErrorBanner } from "../components/ErrorBanner";
import { useFillsList } from "../api/hooks";
import { useWindow } from "../hooks/useWindow";
import { truncateAddress } from "../../../../lib/format";

const DEFAULT_LIMIT = 20;

export function FillsDrilldownPage() {
  const { walletAddress = "" } = useParams();
  const { preset, setPreset, from, to, refresh } = useWindow();
  const [page, setPage] = useState(1);
  const [coinInput, setCoinInput] = useState("");
  const [coin, setCoin] = useState(undefined);
  const [copied, setCopied] = useState(false);

  const queryClient = useQueryClient();

  const fills = useFillsList({
    walletAddress,
    coin,
    from,
    to,
    page,
    limit: DEFAULT_LIMIT,
  });

  const handleRefresh = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ["hl"] });
  };

  const handlePreset = (next) => {
    setPreset(next);
    setPage(1);
  };

  const applyCoin = () => {
    const next = coinInput.trim().toUpperCase() || undefined;
    setCoin(next);
    setPage(1);
  };

  const clearCoin = () => {
    setCoin(undefined);
    setCoinInput("");
    setPage(1);
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop */
    }
  };

  return (
    <div className="space-y-10 md:space-y-12">
      {/* Hero — identity */}
      <header className="min-w-0">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          <span className="h-px w-6 bg-ginox-green" />
          Hyperliquid
        </div>
        <h1 className="mt-3 break-words font-serif text-[34px] italic leading-[1.05] tracking-tightest text-foreground sm:text-[42px] xl:text-[48px]">
          Wallet{" "}
          <span className="text-gradient-ginox">
            {truncateAddress(walletAddress, 8, 6)}
          </span>
        </h1>
        <div className="mt-3 flex items-start gap-2 font-mono text-xs text-muted-foreground">
          <span className="min-w-0 break-all">{walletAddress}</span>
          <button
            type="button"
            onClick={copyAddress}
            className="mt-0.5 shrink-0 text-muted-foreground/60 hover:text-foreground"
            aria-label="Copy address"
          >
            {copied ? (
              <Check className="h-3 w-3 text-ginox-green" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        </div>
      </header>

      {/* Fills — heading, all controls, table */}
      <section className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="font-serif text-[22px] italic text-foreground">
              Fills
            </h2>
            {fills.data && fills.data.pagination.total > 0 ? (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                <span className="text-foreground">
                  {fills.data.pagination.total}
                </span>{" "}
                fills · newest first
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <WindowPicker preset={preset} onChange={handlePreset} />
            <input
              value={coinInput}
              onChange={(e) => setCoinInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyCoin();
              }}
              placeholder="Filter coin"
              maxLength={10}
              className="h-8 w-32 rounded-md border border-border bg-transparent px-3 font-mono text-xs uppercase tracking-wider text-foreground placeholder:text-muted-foreground/40 placeholder:normal-case placeholder:tracking-normal focus:border-ginox-green/40 focus:outline-none"
            />
            {coin ? (
              <Button variant="ghost" size="sm" onClick={clearCoin}>
                Clear
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw
                className={cn(
                  "h-3.5 w-3.5",
                  fills.isFetching && "animate-spin",
                )}
              />
              Refresh
            </Button>
          </div>
        </div>

        <div className="border-t border-border">
          {fills.error ? (
            <div className="py-5">
              <ErrorBanner
                error={fills.error}
                onRetry={() => fills.refetch()}
              />
            </div>
          ) : (
            <>
              <FillsTable
                fills={fills.data?.fills}
                isLoading={fills.isLoading}
              />
              {fills.data ? (
                <div className="border-t border-border">
                  <Pagination
                    data={fills.data.pagination}
                    onChange={setPage}
                    label="fills"
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
